import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Api from "api/Api";
import { addToast } from "store/toastsSlice";

const api = new Api();

const initialState = {
  id: "",
  name: "",
  boardSlug: "",
  color: "",
  language: "",
  publicUrl: null,
  status: {
    fetch: "idle",
  },
  filters: {
    statusFilters: ["NOT_PUBLIC", "OPEN", "REVIEW", "PLANNED", "IN_PROGRESS", "COMPLETE"],
    sortName: "VOTES",
    searchQuery: "",
  },
  ideas: {
    ideas: [],
    status: {
      fetchIdeas: "idle",
      fetchIdea: "idle",
      addIdea: "idle",
      upvoteIdea: "idle",
      downvoteIdea: "idle",
      changeIdeaStatus: "idle",
      editIdea: "idle",
      deleteIdea: "idle",
      declineIdea: "idle",
      listVoters: "idle",
      fetchIdeaComments: "idle",
      addIdeaComment: "idle",
    },
  },
  limits: {
    cappedIdeas: 0,
    status: "idle",
  },
};

export const fetchBoardDetails = createAsyncThunk(
  "boards/fetchBoardDetails",
  async (boardId) => {
    const response = await api.getBoardDetails(boardId);

    return response;
  },
);

export const fetchBoardLimits = createAsyncThunk(
  "boards/fetchBoardLimits",
  async (boardId) => {
    const response = await api.getBoardLimits(boardId);

    return response;
  },
);

export const fetchBoardIdeas = createAsyncThunk(
  "boards/fetchBoardIdeas",
  async (boardId) => {
    const response = await api.getBoardIdeas(boardId);

    return response;
  },
);

export const fetchIdea = createAsyncThunk(
  "boards/fetchIdea",
  async ({ ideaId, boardId }) => {
    const response = await api.getIdea(ideaId, boardId);
    return response;
  },
);

export const listVoters = createAsyncThunk(
  "boards/listVoters",
  async ({ ideaId, boardId }) => {
    const response = await api.listVoters(ideaId, boardId);
    return { ideaId, voters: response };
  },
);

export const upvoteIdea = createAsyncThunk(
  "boards/upvoteIdea",
  async ({ ideaId, boardId }) => {
    const response = await api.giveIdeaUpvote(ideaId, boardId);
    return { ideaId, ...response };
  },
);

export const downvoteIdea = createAsyncThunk(
  "boards/downvoteIdea",
  async ({ ideaId, boardId }) => {
    const response = await api.giveIdeaDownvote(ideaId, boardId);
    return { ideaId, ...response };
  },
);

export const changeIdeaStatus = createAsyncThunk(
  "boards/changeIdeaStatus",
  async ({ status, ideaId, boardId }) => {
    const response = await api.editIdeaStatus({ status }, ideaId, boardId);
    return { ideaId, ...response };
  },
);

export const editIdea = createAsyncThunk(
  "boards/editIdea",
  async ({ data, ideaId, boardId }) => {
    const response = await api.editIdea(data, ideaId, boardId);
    return { ideaId, ...data, ...response };
  },
);

export const deleteIdea = createAsyncThunk(
  "boards/deleteIdea",
  async ({ ideaId, boardId }, { dispatch }) => {
    try {
      await api.deleteIdea(ideaId, boardId);
    } catch (e) {
      // backend returns 204 No Content – ignore JSON parse error
    }
    dispatch(
      addToast({
        type: "info",
        title: "toasts.ideaRemoved.title",
        description: "toasts.ideaRemoved.description",
      }),
    );
    return { ideaId };
  },
);

export const createIdea = createAsyncThunk(
  "boards/createIdea",
  async ({ data, boardId }) => {
    const response = await api.createNewIdea(data, boardId);
    return response;
  },
);

export const declineIdea = createAsyncThunk(
  "boards/declineIdea",
  async ({ data, ideaId, boardId }) => {
    const response = await api.declineIdea(data, ideaId, boardId);
    return { ideaId, ...response };
  },
);

export const fetchIdeaComments = createAsyncThunk(
  "boards/fetchIdeaComments",
  async ({ ideaId, boardId }) => {
    const response = await api.getIdeaComments(ideaId, boardId);
    return { ideaId, comments: response };
  },
);

export const addIdeaComment = createAsyncThunk(
  "boards/addIdeaComment",
  async ({ data, ideaId, boardId }) => {
    const response = await api.addIdeaComment(data, ideaId, boardId);
    return { ideaId, comment: response };
  },
);

const currentBoardSlice = createSlice({
  name: "currentBoard",
  initialState,
  reducers: {
    updateCurrentBoard: (state, action) => {
      state.name = action.payload.name;
      state.language = action.payload.language;
      state.color = action.payload.theme.primary;
      if (action.payload.isPublic !== undefined) {
        state.isPublic = action.payload.isPublic;
      }
      if (action.payload.publicUrl !== undefined) {
        state.publicUrl = action.payload.publicUrl;
      }
    },
    clearCurrentBoard: () => {
      return initialState;
    },
    setStatusFilters: (state, action) => {
      state.filters.statusFilters = action.payload;
    },
    setSort: (state, action) => {
      state.filters.sortName = action.payload;
    },
    setSearchQuery: (state, action) => {
      state.filters.searchQuery = action.payload;
    },
    updateIdea: (state, action) => {
      const idx = state.ideas.ideas.findIndex(
        (i) => i.id === action.payload.id,
      );
      if (idx !== -1) {
        state.ideas.ideas[idx] = {
          ...state.ideas.ideas[idx],
          ...action.payload,
        };
      }
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchBoardDetails.pending, (state, action) => {
        state.status.fetch = "loading";
      })
      .addCase(fetchBoardDetails.fulfilled, (state, action) => {
        state.id = action.payload.id;
        state.name = action.payload.name;
        state.boardSlug = action.payload.boardSlug;
        state.isPublic = action.payload.isPublic;
        state.publicUrl = action.payload.publicUrl;
        state.language = action.payload.language;
        state.color = action.payload.theme.primary || "53d23d";
        state.status.fetch = "succeeded";
      })
      .addCase(fetchBoardDetails.rejected, (state, action) => {
        state.id = "";
        state.status.fetch = "failed";
        state.error.fetch = action.error.message;
      })
      .addCase(fetchBoardLimits.pending, (state) => {
        state.limits.status = "loading";
      })
      .addCase(fetchBoardLimits.fulfilled, (state, action) => {
        Object.assign(state.limits, action.payload);
        state.limits.status = "succeeded";
      })
      .addCase(fetchBoardLimits.rejected, (state, action) => {
        state.limits.status = "failed";
        state.limits.error = action.error.message;
      })
      .addCase(fetchBoardIdeas.pending, (state, action) => {
        state.ideas.status.fetchIdeas = "loading";
      })
      .addCase(fetchBoardIdeas.fulfilled, (state, action) => {
        state.ideas.ideas = action.payload;
        state.ideas.status.fetchIdeas = "succeeded";
      })
      .addCase(fetchBoardIdeas.rejected, (state, action) => {
        state.ideas.status.fetchIdeas = "failed";
        state.ideas.error.fetchIdeas = action.error.message;
      })
      .addCase(fetchIdea.pending, (state) => {
        state.ideas.status.fetchIdea = "loading";
      })
      .addCase(fetchIdea.fulfilled, (state, action) => {
        state.ideas.status.fetchIdea = "succeeded";
        const idx = state.ideas.ideas.findIndex(
          (i) => i.id === action.payload.id,
        );
        if (idx !== -1) {
          const existing = state.ideas.ideas[idx];
          const { commentCount, ...rest } = action.payload;
          state.ideas.ideas[idx] = {
            ...existing,
            ...rest,
            commentCount: existing.commentCount ?? commentCount,
          };
        }
      })
      .addCase(fetchIdea.rejected, (state, action) => {
        state.ideas.status.fetchIdea = "failed";
        state.ideas.error = action.error.message;
      })
      .addCase(listVoters.pending, (state) => {
        state.ideas.status.listVoters = "loading";
      })
      .addCase(listVoters.fulfilled, (state, action) => {
        state.ideas.status.listVoters = "succeeded";
        const idx = state.ideas.ideas.findIndex(
          (i) => i.id === action.payload.ideaId,
        );
        if (idx !== -1) {
          state.ideas.ideas[idx].voters = action.payload.voters;
        }
      })
      .addCase(listVoters.rejected, (state, action) => {
        state.ideas.status.listVoters = "failed";
        state.ideas.error = action.error.message;
      })
      .addCase(upvoteIdea.pending, (state) => {
        state.ideas.status.upvoteIdea = "loading";
      })
      .addCase(upvoteIdea.fulfilled, (state, action) => {
        state.ideas.status.upvoteIdea = "succeeded";
        const idx = state.ideas.ideas.findIndex(
          (i) => i.id === action.payload.ideaId,
        );
        if (idx !== -1) {
          state.ideas.ideas[idx].voteCount = action.payload.voteCount;
          state.ideas.ideas[idx].isVotedByMe = true;
        }
      })
      .addCase(upvoteIdea.rejected, (state, action) => {
        state.ideas.status.upvoteIdea = "failed";
        state.ideas.error = action.error.message;
      })
      .addCase(downvoteIdea.pending, (state) => {
        state.ideas.status.downvoteIdea = "loading";
      })
      .addCase(downvoteIdea.fulfilled, (state, action) => {
        state.ideas.status.downvoteIdea = "succeeded";
        const idx = state.ideas.ideas.findIndex(
          (i) => i.id === action.payload.ideaId,
        );
        if (idx !== -1) {
          state.ideas.ideas[idx].voteCount = action.payload.voteCount;
          state.ideas.ideas[idx].isVotedByMe = false;
        }
      })
      .addCase(downvoteIdea.rejected, (state, action) => {
        state.ideas.status.downvoteIdea = "failed";
        state.ideas.error = action.error.message;
      })
      .addCase(changeIdeaStatus.pending, (state) => {
        state.ideas.status.changeIdeaStatus = "loading";
      })
      .addCase(changeIdeaStatus.fulfilled, (state, action) => {
        state.ideas.status.changeIdeaStatus = "succeeded";
        const idx = state.ideas.ideas.findIndex(
          (i) => i.id === action.payload.ideaId,
        );
        if (idx !== -1) {
          state.ideas.ideas[idx].status = action.payload.status;
        }
      })
      .addCase(changeIdeaStatus.rejected, (state, action) => {
        state.ideas.status.changeIdeaStatus = "failed";
        state.ideas.error = action.error.message;
      })
      .addCase(editIdea.pending, (state) => {
        state.ideas.status.editIdea = "loading";
      })
      .addCase(editIdea.fulfilled, (state, action) => {
        state.ideas.status.editIdea = "succeeded";
        const idx = state.ideas.ideas.findIndex(
          (i) => i.id === action.payload.ideaId,
        );
        if (idx !== -1) {
          state.ideas.ideas[idx] = {
            ...state.ideas.ideas[idx],
            ...action.payload,
          };
        }
      })
      .addCase(editIdea.rejected, (state, action) => {
        state.ideas.status.editIdea = "failed";
        state.ideas.error = action.error.message;
      })
      .addCase(deleteIdea.pending, (state) => {
        state.ideas.status.deleteIdea = "loading";
      })
      .addCase(deleteIdea.fulfilled, (state, action) => {
        state.ideas.status.deleteIdea = "succeeded";
        state.ideas.ideas = state.ideas.ideas.filter(
          (i) => i.id !== action.payload.ideaId,
        );
      })
      .addCase(deleteIdea.rejected, (state, action) => {
        state.ideas.status.deleteIdea = "failed";
        state.ideas.error = action.error.message;
      })
      .addCase(createIdea.pending, (state) => {
        state.ideas.status.addIdea = "loading";
      })
      .addCase(createIdea.fulfilled, (state, action) => {
        state.ideas.status.addIdea = "succeeded";
        state.ideas.ideas.unshift(action.payload);
      })
      .addCase(createIdea.rejected, (state, action) => {
        state.ideas.status.addIdea = "failed";
        state.ideas.error = action.error.message;
      })
      .addCase(declineIdea.pending, (state) => {
        state.ideas.status.declineIdea = "loading";
      })
      .addCase(declineIdea.fulfilled, (state, action) => {
        state.ideas.status.declineIdea = "succeeded";
        const idx = state.ideas.ideas.findIndex(
          (i) => i.id === action.payload.ideaId,
        );
        if (idx !== -1) {
          state.ideas.ideas[idx].status = "DECLINED";
          if (action.payload.declineDetails) {
            state.ideas.ideas[idx].declineDetails = action.payload.declineDetails;
          }
        }
      })
      .addCase(declineIdea.rejected, (state, action) => {
        state.ideas.status.declineIdea = "failed";
        state.ideas.error = action.error.message;
      })
      .addCase(fetchIdeaComments.pending, (state) => {
        state.ideas.status.fetchIdeaComments = "loading";
      })
      .addCase(fetchIdeaComments.fulfilled, (state, action) => {
        state.ideas.status.fetchIdeaComments = "succeeded";
        const idx = state.ideas.ideas.findIndex(
          (i) => i.id === action.payload.ideaId,
        );
        if (idx !== -1) {
          state.ideas.ideas[idx].comments = action.payload.comments;
        }
      })
      .addCase(fetchIdeaComments.rejected, (state, action) => {
        state.ideas.status.fetchIdeaComments = "failed";
        state.ideas.error = action.error.message;
      })
      .addCase(addIdeaComment.pending, (state) => {
        state.ideas.status.addIdeaComment = "loading";
      })
      .addCase(addIdeaComment.fulfilled, (state, action) => {
        state.ideas.status.addIdeaComment = "succeeded";
        const idx = state.ideas.ideas.findIndex(
          (i) => i.id === action.payload.ideaId,
        );
        if (idx !== -1) {
          const comments = state.ideas.ideas[idx].comments ?? [];
          state.ideas.ideas[idx].comments = [...comments, action.payload.comment];
          state.ideas.ideas[idx].commentCount =
            (state.ideas.ideas[idx].commentCount ?? 0) + 1;
        }
      })
      .addCase(addIdeaComment.rejected, (state, action) => {
        state.ideas.status.addIdeaComment = "failed";
        state.ideas.error = action.error.message;
      });
  },
});

export const { clearCurrentBoard, updateCurrentBoard, updateIdea, setStatusFilters, setSort, setSearchQuery } =
  currentBoardSlice.actions;

export const selectCurrentBoardData = (state) => state.currentBoard;
export const selectIdeaById = (state, ideaId) =>
  state.currentBoard.ideas.ideas.find((i) => i.id === ideaId);
export const selectIdeasStatus = (state) => state.currentBoard.ideas.status;
export const selectFilters = (state) => state.currentBoard.filters;

export default currentBoardSlice.reducer;
